import React from "react";
import { NavLink } from "react-router-dom";
import { Gift, Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useI18n } from "../../lib/i18n";

/**
 * Search · AZ · Account · Bag, read as one group rather than four loose icons.
 *
 * The 44px pointer target is padding rather than box size, so the icons sit the
 * intended 20px apart while staying comfortably tappable.
 */
export default function HeaderActions({
  searchOpen, onToggleSearch,
  locale, onToggleLocale,
  user, itemCount,
  mobileOpen, onToggleMobile,
  compact = false,
}) {
  const { t } = useI18n();
  const iconProps = { size: 18, strokeWidth: 1.3, "aria-hidden": "true" };

  return (
    <div className="adf-header-utilities" data-testid="header-actions">
      {/* A button rather than another menu entry: a gift card is a purchase in
          its own right, not a department to browse, and it is the one thing a
          visitor arrives already meaning to buy. Hidden when the bar condenses
          on scroll, where only the essentials stay. */}
      {!compact && (
        <NavLink
          to="/gift-card"
          data-testid="nav-gift-card"
          className="adf-util adf-util--gift"
        >
          <Gift size={15} strokeWidth={1.4} aria-hidden="true" />
          <span>{t("nav.gift_card", "Hədiyyə kartı")}</span>
        </NavLink>
      )}

      {!compact && (
        <button
          type="button"
          data-testid="search-toggle"
          aria-label={t("nav.search", "Axtarış")}
          aria-expanded={searchOpen}
          onClick={onToggleSearch}
          className="adf-util adf-util--icon adf-util--mobile-hide"
        >
          {searchOpen ? <X {...iconProps} /> : <Search {...iconProps} />}
        </button>
      )}

      {/* Text, not an icon: a language is a word, and it anchors the group. */}
      <button
        type="button"
        data-testid="locale-toggle"
        onClick={onToggleLocale}
        aria-label={t("nav.language", "Dil")}
        className="adf-util adf-util--locale"
      >
        {locale.toUpperCase()}
      </button>

      {!compact && (
        <NavLink
          to={user ? "/account" : "/auth/login"}
          data-testid="nav-account"
          aria-label={t("nav.account", "Hesab")}
          className="adf-util adf-util--icon adf-util--account"
        >
          <User {...iconProps} />
        </NavLink>
      )}

      {/* Straight to the saved wardrobe — the PDP heart saves a piece, this is
          where a shopper goes to see what they kept. Guests are sent to sign-in
          and returned to the wardrobe tab. */}
      {!compact && (
        <NavLink
          to={user ? "/account?tab=wardrobe" : `/auth/login?next=${encodeURIComponent("/account?tab=wardrobe")}`}
          data-testid="nav-wardrobe"
          aria-label={t("nav.wardrobe", "Qarderob")}
          className="adf-util adf-util--icon adf-util--wardrobe adf-util--mobile-hide"
        >
          <Heart {...iconProps} />
        </NavLink>
      )}

      <NavLink
        to="/cart"
        data-testid="nav-cart"
        aria-label={t("nav.cart", "Səbət")}
        className="adf-util adf-util--icon relative adf-util--mobile-hide"
      >
        <ShoppingBag {...iconProps} />
        {itemCount > 0 && (
          <span className="adf-util-badge">{itemCount > 9 ? "9+" : itemCount}</span>
        )}
      </NavLink>

      {onToggleMobile && (
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={onToggleMobile}
          aria-label={mobileOpen ? t("nav.menu_close", "Menyunu bağla") : t("nav.menu_open", "Menyunu aç")}
          aria-expanded={mobileOpen}
          aria-controls="adf-mobile-sheet"
          className="adf-util adf-util--icon adf-util--burger"
        >
          {mobileOpen ? <X {...iconProps} /> : <Menu {...iconProps} />}
        </button>
      )}
    </div>
  );
}
